import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Flex, Input, InputNumber, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ImportOutlined, LockOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDiscoveredChannels } from '../../hooks/useDiscoveredChannels';
import {
  DISCOVERED_CHANNEL_STATUSES,
  DiscoveredChannelEntity,
  DiscoveredChannelKind,
  DiscoveredChannelSort,
  DiscoveredChannelStatus,
  canImportDiscoveredChannel,
  canRejectDiscoveredChannel,
} from '../../../domain/entities/DiscoveredChannel';

import './AdminDiscoveredChannelsPage.styles.css';

const STATUS_COLORS: Record<DiscoveredChannelStatus, string> = {
  new: 'default',
  verified: 'success',
  unavailable: 'warning',
  not_a_channel: 'default',
  rejected: 'error',
  imported: 'purple',
};

type CatalogFilter = 'all' | 'in' | 'out';

const shortLink = (link: string) => link.replace(/^https:\/\/t\.me\//, '');

const AdminDiscoveredChannelsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { items, total, page, perPage, filters, isLoading, isSaving, error, load, setFilters, setPage, importToCatalog, reject } = useDiscoveredChannels();
  const [selected, setSelected] = useState<DiscoveredChannelEntity[]>([]);

  const numberFormat = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }), [i18n.language]);

  const showError = (e: unknown) => {
    const slug = e instanceof Error ? e.message : 'unknown-error';
    message.error(t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) }));
  };

  const handleImport = async () => {
    try {
      const draftUuid = await importToCatalog(selected);
      setSelected([]);
      navigate(`/admin/channels/import?draft=${draftUuid}`);
    } catch (e) {
      showError(e);
    }
  };

  const handleReject = async () => {
    try {
      await reject(selected);
      setSelected([]);
      message.success(t('discovered-channels-rejected', { ns: 'messages' }));
    } catch (e) {
      showError(e);
    }
  };

  const catalogFilter: CatalogFilter = filters.inCatalog === undefined ? 'all' : filters.inCatalog ? 'in' : 'out';
  const statusLabel = (status: DiscoveredChannelStatus) => t(`discovered-status-${status}`, { ns: 'labels' });
  const kindLabel = (kind: DiscoveredChannelKind) => (kind ? t(`discovered-kind-${kind}`, { ns: 'labels' }) : '—');

  const columns: ColumnsType<DiscoveredChannelEntity> = [
    {
      title: t('discovered-channel', { ns: 'labels' }),
      key: 'channel',
      render: (_, channel) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{channel.title || shortLink(channel.link)}</Typography.Text>
          <Space size={4}>
            {channel.isPrivate && <LockOutlined className="admin-discovered-private-icon" />}
            <Typography.Link href={channel.link} target="_blank" rel="noreferrer">{shortLink(channel.link)}</Typography.Link>
          </Space>
        </Space>
      ),
    },
    {
      title: t('discovered-kind', { ns: 'labels' }),
      key: 'kind',
      render: (_, channel) => kindLabel(channel.kind),
    },
    {
      title: t('discovered-members', { ns: 'labels' }),
      dataIndex: 'membersCount',
      key: 'membersCount',
      align: 'right',
      render: (value: number, channel) => (channel.status === 'verified' || channel.status === 'imported' ? numberFormat.format(value) : '—'),
    },
    {
      title: t('discovered-mentions', { ns: 'labels' }),
      dataIndex: 'mentionsCount',
      key: 'mentionsCount',
      align: 'right',
    },
    {
      title: t('discovered-status', { ns: 'labels' }),
      key: 'status',
      render: (_, channel) => {
        const tag = <Tag color={STATUS_COLORS[channel.status]}>{statusLabel(channel.status)}</Tag>;
        return (
          <Space size={4} wrap>
            {channel.verifyError ? <Tooltip title={channel.verifyError}>{tag}</Tooltip> : tag}
            {channel.inCatalog && <Tag color="blue">{t('discovered-in-catalog', { ns: 'labels' })}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('discovered-found-in', { ns: 'labels' }),
      key: 'source',
      render: (_, channel) => (
        <Typography.Link href={channel.firstSourceMessageLink} target="_blank" rel="noreferrer">
          {shortLink(channel.firstSourceMessageLink)}
        </Typography.Link>
      ),
    },
    {
      title: t('discovered-last-seen', { ns: 'labels' }),
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: (value: string) => dateFormat.format(new Date(value)),
    },
  ];

  const importable = selected.length > 0 && selected.every(canImportDiscoveredChannel);
  const rejectable = selected.some(canRejectDiscoveredChannel);

  return (
    <div className="admin-discovered-page">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
        <div>
          <h1 className="admin-discovered-title">{t('admin-discovered-channels', { ns: 'titles' })}</h1>
          <p className="admin-discovered-subtitle">{t('admin-discovered-channels-subtitle', { ns: 'titles' })}</p>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => load()} loading={isLoading}>{t('refresh', { ns: 'buttons' })}</Button>
          <Popconfirm
            title={t('discovered-reject-confirm', { ns: 'modals', count: selected.length })}
            okText={t('discovered-reject', { ns: 'buttons' })}
            cancelText={t('cancel', { ns: 'buttons' })}
            okButtonProps={{ danger: true }}
            disabled={!rejectable}
            onConfirm={handleReject}
          >
            <Button danger icon={<StopOutlined />} disabled={!rejectable || isSaving}>{t('discovered-reject', { ns: 'buttons' })}</Button>
          </Popconfirm>
          <Tooltip title={importable || selected.length === 0 ? '' : t('discovered-import-only-verified', { ns: 'standard' })}>
            <Button type="primary" icon={<ImportOutlined />} disabled={!importable} loading={isSaving} onClick={handleImport}>
              {t('discovered-import', { ns: 'buttons', count: selected.length })}
            </Button>
          </Tooltip>
        </Space>
      </Flex>

      <Flex className="admin-discovered-filters" gap={12} wrap="wrap">
        <Input.Search
          allowClear
          className="admin-discovered-search"
          placeholder={t('discovered-search', { ns: 'labels' })}
          defaultValue={filters.search}
          onSearch={(search) => setFilters({ ...filters, search })}
        />
        <Select
          mode="multiple"
          allowClear
          className="admin-discovered-filter"
          placeholder={t('discovered-status', { ns: 'labels' })}
          value={filters.statuses}
          options={DISCOVERED_CHANNEL_STATUSES.map((status) => ({ value: status, label: statusLabel(status) }))}
          maxTagCount="responsive"
          onChange={(statuses: DiscoveredChannelStatus[]) => setFilters({ ...filters, statuses })}
        />
        <Select
          mode="multiple"
          allowClear
          className="admin-discovered-filter"
          placeholder={t('discovered-kind', { ns: 'labels' })}
          value={filters.kinds}
          options={(['megagroup', 'broadcast'] as DiscoveredChannelKind[]).map((kind) => ({ value: kind, label: kindLabel(kind) }))}
          onChange={(kinds: DiscoveredChannelKind[]) => setFilters({ ...filters, kinds })}
        />
        <Select
          className="admin-discovered-filter-narrow"
          value={catalogFilter}
          options={[
            { value: 'all', label: t('discovered-catalog-all', { ns: 'labels' }) },
            { value: 'out', label: t('discovered-catalog-out', { ns: 'labels' }) },
            { value: 'in', label: t('discovered-catalog-in', { ns: 'labels' }) },
          ]}
          onChange={(value: CatalogFilter) => setFilters({ ...filters, inCatalog: value === 'all' ? undefined : value === 'in' })}
        />
        <InputNumber
          min={0}
          step={100}
          className="admin-discovered-filter-narrow"
          placeholder={t('discovered-min-members', { ns: 'labels' })}
          value={filters.minMembers}
          onChange={(value) => setFilters({ ...filters, minMembers: value ?? undefined })}
        />
        <Select
          className="admin-discovered-filter-narrow"
          value={filters.sort}
          options={(['members', 'mentions', 'last_seen'] as DiscoveredChannelSort[]).map((sort) => ({ value: sort, label: t(`discovered-sort-${sort}`, { ns: 'labels' }) }))}
          onChange={(sort: DiscoveredChannelSort) => setFilters({ ...filters, sort })}
        />
      </Flex>

      {error && <Alert type="error" showIcon message={t(error, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) })} />}

      <Table<DiscoveredChannelEntity>
        rowKey={(channel) => channel.id}
        columns={columns}
        dataSource={items}
        loading={isLoading}
        scroll={{ x: 1000 }}
        rowSelection={{
          selectedRowKeys: selected.map((channel) => channel.id),
          preserveSelectedRowKeys: true,
          onChange: (_, rows) => setSelected(rows),
        }}
        locale={{ emptyText: t('discovered-empty', { ns: 'labels' }) }}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          showSizeChanger: true,
          showTotal: (count) => t('discovered-total', { ns: 'labels', count }),
          onChange: (nextPage, nextPerPage) => setPage(nextPage, nextPerPage),
        }}
      />
    </div>
  );
};

export default AdminDiscoveredChannelsPage;
